import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { api, type Query } from "../../api/client.ts";
import type {
  Crumb,
  Field,
  FolderList,
  FolderOut,
  FolderRow,
  ItemCounts,
  ItemDetail,
  Member,
  ObjEntity,
  PageItemRow,
  SpaceOut,
} from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { patchItem } from "../../lib/data.ts";
import { optionLabel } from "../../lib/fieldTypes.ts";
import {
  crumbsOf,
  type FolderIndex,
  folderHref,
  isFolderId,
  isWithin,
  placeOf,
  placeQuery,
} from "../../lib/folders.ts";
import { formatNumber } from "../../lib/format.ts";
import { itemFilters } from "../../lib/itemQuery.ts";
import { spaceApi } from "../../lib/paths.ts";
import { asApiError, bump, useAction, useResource } from "../../lib/resource.ts";
import { canEdit } from "../../lib/roles.ts";
import { location } from "../../lib/router.ts";
import { toast } from "../../lib/toast.ts";
import { Button, Link } from "../../ui/Button.tsx";
import { FolderIcon, NAV_ICONS } from "../../ui/icons.tsx";
import { Async } from "../../ui/misc.tsx";
import { LINK } from "../../ui/styles.ts";
import { FolderCards } from "../folders/cards.tsx";
import { folderAsk } from "../folders/listing.ts";
import { ItemCard } from "./cards.tsx";
import { PeekButton } from "./icons.tsx";

/** Cards a column loads at first, and again on each "Load more" (up to the API's page size). */
const STEP = 20;
const MOST = 200;
/** A board's place: as the list's (what's right here, or with "Flatten subfolders" everything under it). */
const boardPlace = (sp: URLSearchParams) => placeQuery(placeOf(sp));

type Row = PageItemRow["data"][number];

/** A column: the items with one value of the field, or (null) with none. */
type Column = { value: string | null; count: number };

interface BoardProps {
  space: SpaceOut;
  entity: ObjEntity;
  field: Field;
  sort: string;
  members: readonly Member[] | undefined;
  /** Where each item is, when listed from under several folders (flattened). */
  pathOf?: ((row: Row) => readonly Crumb[] | undefined) | undefined;
  /** The folders here, a column of links before the board's (not flattened). */
  folders?: readonly FolderRow[] | undefined;
}

/** The `where` condition of a column's items. */
function condition(field: Field, value: string | null): string {
  if (value === null) {
    return `data.${field.key}:exists:false`;
  }
  return `data.${field.key}:${field.many ? "contains" : "eq"}:${value}`;
}

/** The field's values on an item (a multiple-choice field has several). */
function valuesOf(field: Field, row: Row): string[] {
  const v = row.data?.[field.key];
  return (Array.isArray(v) ? v : [v]).filter((x): x is string => typeof x === "string" && x !== "");
}

/** The columns: the field's options in order, then values items have that aren't options, then "no value". */
function columns(field: Field, counts: ItemCounts): Column[] {
  const n = new Map(counts.values.map((c) => [c.value, c.count]));
  const options = field.options ?? [];
  const extra = counts.values.map((c) => c.value).filter((v) => !options.includes(v));
  return [
    ...[...options, ...extra].map((value) => ({ value, count: n.get(value) ?? 0 })),
    { value: null, count: counts.none },
  ];
}

interface ColumnProps extends BoardProps {
  column: Column;
  hover: string | null;
  onHover: (id: string | null) => void;
  /** Moves an item here (single-choice fields), or undefined when items can't be moved. */
  onDrop: ((id: string) => void) | undefined;
}

/** A column's frame: its title and count, and (`onDrop`) cards dropped on it. */
function ColumnFrame(p: {
  title: ComponentChildren;
  label: string;
  /** Its title is no value's (in italics). */
  muted?: boolean;
  count: number | undefined;
  onDrop: ((id: string) => void) | undefined;
  children: ComponentChildren;
}) {
  const [over, setOver] = useState(false);
  const drop = p.onDrop;
  return (
    <section
      aria-label={p.label}
      class={`flex w-72 shrink-0 flex-col gap-2 rounded-lg p-2 ${over ? "bg-blue-50 ring-2 ring-blue-300" : "bg-zinc-100"}`}
      onDragOver={
        drop
          ? (e) => {
              e.preventDefault();
              setOver(true);
            }
          : undefined
      }
      onDragLeave={drop ? () => setOver(false) : undefined}
      onDrop={
        drop
          ? (e) => {
              e.preventDefault();
              setOver(false);
              const id = e.dataTransfer?.getData("text/plain");
              if (id) {
                drop(id);
              }
            }
          : undefined
      }
    >
      <h2 class="flex items-center justify-between gap-2 px-1 text-sm font-medium text-zinc-700">
        <span
          class={`flex min-w-0 items-center gap-1.5 [overflow-wrap:anywhere] ${p.muted ? "text-zinc-500 italic" : ""}`}
        >
          {p.title}
        </span>
        {p.count === undefined ? null : <span class="text-xs text-zinc-500 tabular-nums">{formatNumber(p.count)}</span>}
      </h2>
      {p.children}
    </section>
  );
}

/** What each card of a column shows besides the item: hidden field, copies, where it is. */
interface CardsOf {
  space: SpaceOut;
  entity: ObjEntity | undefined;
  entities: readonly ObjEntity[];
  members: readonly Member[] | undefined;
  hide: string;
  copies: (row: Row) => number;
  hover: string | null;
  onHover: (id: string | null) => void;
  draggable: boolean;
  /** Where an item is, when the column doesn't say it (flattened, or deeper than the column's folder). */
  pathOf?: ((row: Row) => readonly Crumb[] | undefined) | undefined;
}

/** A column's cards, `query` matching them, a few at a time; `onTotal` hears how many match. */
function ColumnCards(
  p: CardsOf & { query: Query; cacheKey: string; sort: string; onTotal?: (n: number | undefined) => void },
) {
  const [limit, setLimit] = useState(STEP);
  const list = useResource(`board:${p.space.id}?${p.cacheKey}&${p.sort}&${limit}`, (signal) =>
    api.get<PageItemRow>(spaceApi(p.space, "/items"), { ...p.query, sort: p.sort, limit }, signal),
  );
  const total = list.data?.total ?? undefined;
  const onTotal = p.onTotal;
  useEffect(() => onTotal?.(total), [onTotal, total]);
  return (
    <Async r={list}>
      {(page) => (
        <>
          {page.data.map((row) => {
            const copies = p.copies(row);
            return (
              <ItemCard
                key={row.id}
                space={p.space}
                row={row}
                entity={p.entity}
                entities={p.entities}
                members={p.members}
                board={{
                  hide: p.hide,
                  copies,
                  lit: copies > 0 && p.hover === row.id,
                  onHover: p.onHover,
                  draggable: p.draggable,
                  path: p.pathOf?.(row),
                }}
              />
            );
          })}
          {page.data.length === 0 ? (
            <div class="min-h-32 flex-1 rounded-md border-2 border-dashed border-zinc-300" />
          ) : null}
          {page.next && limit < MOST ? (
            <Button variant="ghost" onClick={() => setLimit(Math.min(limit + STEP, MOST))}>
              {t("items.board.loadMore")}
            </Button>
          ) : null}
        </>
      )}
    </Async>
  );
}

function BoardColumn(p: ColumnProps) {
  const { field, column } = p;
  const sp = location.value.searchParams;
  const where = condition(field, column.value);
  const title = column.value === null ? t("items.board.noValue") : optionLabel(field, column.value);
  return (
    <ColumnFrame title={title} label={title} muted={column.value === null} count={column.count} onDrop={p.onDrop}>
      <ColumnCards
        space={p.space}
        entity={p.entity}
        entities={[]}
        members={p.members}
        hide={field.key}
        copies={(row) => (field.many ? Math.max(0, valuesOf(field, row).length - 1) : 0)}
        hover={p.hover}
        onHover={p.onHover}
        draggable={p.onDrop !== undefined}
        pathOf={p.pathOf}
        query={{ ...itemFilters(sp, [where]), ...boardPlace(sp) }}
        cacheKey={`${sp.toString()}&${where}`}
        sort={p.sort}
      />
    </ColumnFrame>
  );
}

/**
 * Items as a board: a column per value of a choice field (its options, any other values items have, and "no value"),
 * each with its total and its first cards. Cards of a single-choice field move by dragging them to another column;
 * an item with several values of a multiple-choice field shows in each of their columns, its copies marked and lit
 * together.
 */
export function Board(p: BoardProps) {
  const sp = location.value.searchParams;
  const [hover, setHover] = useState<string | null>(null);
  const by = `data.${p.field.key}`;
  const counts = useResource(`board-counts:${p.space.id}?${sp.toString()}&${by}`, (signal) =>
    api.get<ItemCounts>(
      spaceApi(p.space, "/items/counts"),
      { ...itemFilters(sp), ...boardPlace(sp), entity: p.entity.id, by },
      signal,
    ),
  );
  const [move] = useAction(async (id: string, value: string | null) => {
    // A folder's card (the column of them) moves into folders, not onto a value.
    if (isFolderId(id)) {
      return;
    }
    const item = await api.get<ItemDetail>(spaceApi(p.space, `/items/${id}`));
    if ((item.data?.[p.field.key] ?? null) === value) {
      return;
    }
    await api.patch(spaceApi(p.space, `/items/${id}`), { data: { [p.field.key]: value }, ver: item.ver });
    bump();
  });
  const movable = !p.field.many && canEdit(p.space);
  return (
    <Async r={counts}>
      {(c) => (
        // Relative: what's positioned in a card (screen-reader text) stays inside the scroll, not widening the page.
        // Columns as tall as the tallest, so any of them is easy to drop on.
        <div class="relative -mx-4 flex gap-3 overflow-x-auto px-4 pb-4 sm:mx-0 sm:px-0">
          {p.folders && p.folders.length > 0 ? (
            <FolderCards space={p.space} folders={p.folders} layout="column" draggable={canEdit(p.space)} />
          ) : null}
          {columns(p.field, c).map((column) => (
            <BoardColumn
              key={`${p.field.key}:${column.value ?? ""}`}
              {...p}
              column={column}
              hover={hover}
              onHover={setHover}
              onDrop={movable ? (id) => move(id, column.value) : undefined}
            />
          ))}
        </div>
      )}
    </Async>
  );
}

/** The "Here" column's value: the items right in the place, in no subfolder. */
const HERE = "here";

/** What a folder board's columns share. */
interface FolderColumns {
  cards: Omit<CardsOf, "pathOf">;
  filters: Query;
  sort: string;
  /** Everything under each column's folder ("Flatten subfolders"), or only what's right in it. */
  flat: boolean;
  index: FolderIndex | undefined;
  unmatched: boolean;
}

/**
 * A subfolder's column: its items, everything under it when flattened (deeper cards saying where); else only those
 * right in it, its own subfolders on top (cards, to go into them); how many, as listed.
 */
function FolderColumn(p: FolderColumns & { folder: FolderRow; onDrop: ((id: string) => void) | undefined }) {
  const { folder: f, cards } = p;
  const sp = location.value.searchParams;
  const [total, setTotal] = useState<number | undefined>(undefined);
  const ask = folderAsk(p.filters, p.unmatched);
  const subs = useResource(
    `board-subs:${cards.space.id}:${f.id}:${JSON.stringify(p.filters)}:${ask.key}:${p.flat}`,
    (s) =>
      !p.flat && f.folders > 0
        ? api.get<FolderList>(spaceApi(cards.space, "/folders"), { ...p.filters, parent: f.id, ...ask.query }, s)
        : Promise.resolve({ data: [], path: [] } as FolderList),
  );
  return (
    <ColumnFrame
      title={
        <>
          <PeekButton
            space={cards.space}
            id={f.id}
            name={f.name}
            icon={<FolderIcon size={16} class="text-zinc-500" />}
          />
          <Link href={folderHref(cards.space, f.id, sp)} class={`${LINK} min-w-0`}>
            {f.name}
          </Link>
        </>
      }
      label={f.name}
      count={p.flat ? f.count : total}
      onDrop={p.onDrop}
    >
      {subs.data ? (
        <FolderCards
          space={cards.space}
          folders={ask.keep(subs.data.data)}
          layout="stack"
          draggable={cards.draggable}
        />
      ) : null}
      <ColumnCards
        {...cards}
        pathOf={(row) => (row.folder && row.folder !== f.id ? crumbsOf(p.index, row.folder).slice(-2) : undefined)}
        query={{ ...p.filters, folder: f.id, ...(p.flat ? { deep: true } : {}) }}
        cacheKey={`${sp.toString()}&${f.id}&${p.flat}`}
        sort={p.sort}
        onTotal={setTotal}
      />
    </ColumnFrame>
  );
}

/**
 * Items as a board of folders: a column of what's right here (in no subfolder of it), then one per subfolder (as the
 * list lists them: those holding matches, unless asked for all), each with its subfolders on top and its items.
 * Dragging a card (an item's, or a subfolder's) to a column moves it into that folder. Dragging a card to a
 * column moves the item into that folder (its sharing kept, as moves do).
 */
export function FolderBoard(p: {
  space: SpaceOut;
  entity: ObjEntity | undefined;
  entities: readonly ObjEntity[];
  sort: string;
  members: readonly Member[] | undefined;
  /** The place's folder (none: the root). */
  here: string | null;
  folders: readonly FolderRow[];
  index: FolderIndex | undefined;
  /** "Flatten subfolders": each column with everything under its folder. */
  flat: boolean;
  /** Filtered, subfolders holding no match listed too. */
  unmatched: boolean;
}) {
  const sp = location.value.searchParams;
  const [hover, setHover] = useState<string | null>(null);
  const [hereTotal, setHereTotal] = useState<number | undefined>(undefined);
  const [move] = useAction(async (id: string, to: string | null) => {
    if (isFolderId(id)) {
      await moveFolder(id, to);
      return;
    }
    const item = await api.get<ItemDetail>(spaceApi(p.space, `/items/${id}`));
    const at = item.folder ?? null;
    // Already there (or, for a subfolder's column, somewhere under it).
    if (at === to || (to !== null && at !== null && to !== p.here && isWithin(p.index, at, to))) {
      return;
    }
    await patchItem(p.space, item, { folder: to });
    bump();
  });
  /** A subfolder's card dropped on a column: moved into its folder (not into itself, nor where it is). */
  const moveFolder = async (id: string, to: string | null) => {
    if (id === to || (to !== null && isWithin(p.index, to, id))) {
      return;
    }
    const f = await api.get<FolderOut>(spaceApi(p.space, `/folders/${id}`));
    if ((f.parent ?? null) === to) {
      return;
    }
    try {
      await api.patch<FolderOut>(spaceApi(p.space, `/folders/${id}`), { parent: to, ver: f.ver });
    } catch (e) {
      // One of its name there: said, nothing moved (Move to… offers renaming or merging).
      if (asApiError(e).code === "name_taken") {
        toast(t("folders.taken", { name: f.name }), "error");
        return;
      }
      throw e;
    }
    bump();
  };
  const movable = canEdit(p.space);
  const filters = itemFilters(sp);
  const cards = {
    space: p.space,
    entity: p.entity,
    entities: p.entities,
    members: p.members,
    hide: "",
    copies: () => 0,
    hover,
    onHover: setHover,
    draggable: movable,
  };
  const shared: FolderColumns = {
    cards,
    filters,
    sort: p.sort,
    flat: p.flat,
    index: p.index,
    unmatched: p.unmatched,
  };
  const hereLabel = p.here ? (p.index?.byId.get(p.here)?.name ?? t("folders.root")) : t("folders.root");
  return (
    <div class="relative -mx-4 flex gap-3 overflow-x-auto px-4 pb-4 sm:mx-0 sm:px-0">
      <ColumnFrame
        title={
          <>
            {/* Where it is: the space's items (as in the sidebar), or the folder. */}
            {p.here ? (
              <FolderIcon size={16} class="shrink-0 text-zinc-500" />
            ) : (
              <NAV_ICONS.items size={16} class="shrink-0 text-zinc-500" />
            )}
            {t("items.board.here", { name: hereLabel })}
          </>
        }
        label={t("items.board.here", { name: hereLabel })}
        count={hereTotal}
        onDrop={movable ? (id) => move(id, p.here) : undefined}
      >
        <ColumnCards
          {...cards}
          query={{ ...filters, folder: p.here ?? "root" }}
          cacheKey={`${sp.toString()}&${HERE}`}
          sort={p.sort}
          onTotal={setHereTotal}
        />
      </ColumnFrame>
      {p.folders.map((f) => (
        <FolderColumn key={f.id} {...shared} folder={f} onDrop={movable ? (id) => move(id, f.id) : undefined} />
      ))}
    </div>
  );
}
